import { Block, Event, LinkOff } from "@mui/icons-material";
import { Box, Chip, Tooltip } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { JumpingDots } from "../../../../../../components/UIToolkit/Animations/JumpingDots";
import { SystemDataGrid } from "../../../../../../components/UIToolkit/Tables/SystemDataGrid";
import type { PtoTypeRow } from "./usePtoTypeRows";

interface HrisTimeOffOverviewProps {
	rows: PtoTypeRow[];
	/** Set of rawStrings whose save is debounced/in flight — drives the per-row loading dots. */
	pendingKeys: Set<string>;
}

/**
 * Right pane of the Time Off Pretty Mapping view. Read-only summary grid:
 *   HRIS TIME OFF TYPE | MAPPED TO
 *
 * Consumes the same `PtoTypeRow[]` as the left pane (already filtered by the parent's search). Editing
 * happens in the left pane — the Time Off analog of `HrisJobOverview`.
 */
export const HrisTimeOffOverview: FC<HrisTimeOffOverviewProps> = ({ rows, pendingKeys }) => {
	const { t } = useTranslation();

	const columns: GridColDef<PtoTypeRow>[] = useMemo(
		() => [
			{
				field: "rawString",
				headerName: t("hris.mapping.column.hrisTimeOffType"),
				flex: 1,
				minWidth: 200,
			},
			{
				field: "ptoType",
				headerName: t("hris.mapping.column.mappedTo"),
				flex: 1,
				minWidth: 200,
				sortable: false,
				display: "flex",
				renderCell: (params) => {
					const row = params.row;
					if (pendingKeys.has(row.rawString)) {
						return <JumpingDots data-testid={`hris-mapping-time-off-overview-pending-${row.rawString}`} />;
					}
					if (row.isUnmapped) {
						return (
							<Tooltip title={t("hris.mapping.notMapped.tooltip")}>
								<Chip
									size="small"
									icon={<LinkOff />}
									label={t("hris.mapping.notMapped")}
									variant="outlined"
									color="warning"
									data-testid={`hris-mapping-time-off-overview-not-mapped-${row.rawString}`}
								/>
							</Tooltip>
						);
					}
					if (row.ptoType === null) {
						return (
							<Chip
								size="small"
								icon={<Block />}
								label={t("hris.mapping.excluded")}
								variant="outlined"
								color="error"
								data-testid={`hris-mapping-time-off-overview-dont-sync-${row.rawString}`}
							/>
						);
					}
					const label = row.reason ? `${row.ptoType} · ${row.reason}` : row.ptoType;
					return <Chip size="small" icon={<Event />} label={label} variant="outlined" color="primary" />;
				},
			},
		],
		[t, pendingKeys],
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }} data-testid="hris-mapping-time-off-overview-pane">
			<Box sx={{ flex: 1, minHeight: 0 }}>
				<SystemDataGrid<PtoTypeRow> rows={rows} columns={columns} getRowId={(row) => row.rawString} dataTestId="hris-mapping-time-off-overview" />
			</Box>
		</Box>
	);
};
